import React from 'react';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import { trackEventFromState } from '@eb/site-analytics';

import { AlertChunky } from '@eb/eds-iconography';

import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eb/social-login-facebook';

import {
    sendSoftAccountActivationEmail,
    setAccountExistent,
    updateFormType,
    showAuthNotification,
    clearAuthNotification,
} from './auth';

import {
    GA_AUTH_CATEGORY,
    GA_ENTER_EMAIL_EVENT,
    GA_EXISTING_ACCT_LABEL,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE_FB,
    GA_NEW_ACCT_LABEL,
    GA_SOFT_ACCT_LABEL,
    SIGN_UP_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_LINK_LOGIN_PATH,
    LOOKUP_SOCIAL_USER_EXISTENT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
} from '../constants';

import {
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
} from '../constants/messages';

import { isLoginProviderEnabled } from '../utils';

import { lookupUser as lookupUserApi } from '../api';

/** ACTIONS **/
export const lookupUser = ({ email, socialProvider }) => (
    dispatch,
    getState,
) => {
    if (email) {
        return lookupUserApi(email).then((response) => {
            if (socialProvider) {
                return handleLookupUserResponseSocial({
                    response,
                    dispatch,
                    getState,
                    email,
                    socialProvider,
                }).catch((error) => Promise.reject(error));
            } else {
                handleLookupUserResponse(response, dispatch, getState, email);
            }
        });
    } else {
        dispatch(clearAuthNotification());

        if (socialProvider) {
            return Promise.reject();
        }
    }
};

/** RESPONSE HANDLERS **/
export const handleLookupUserResponse = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId) {
        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEventFromState(state, {
                action: GA_ENTER_EMAIL_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_EXISTING_ACCT_LABEL,
            });
            dispatch(clearAuthNotification());
            // dispatch(enableSigninButton())
        } else if (
            hasFbAndAppleAccountOnly({
                socialSSOSettings,

                signInMethods,
            })
        ) {
            trackEventFromState(state, {
                action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE_FB,
                category: GA_AUTH_CATEGORY,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
        } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            trackEventFromState(state, {
                action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
                category: GA_AUTH_CATEGORY,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
        } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            trackEventFromState(state, {
                action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
                category: GA_AUTH_CATEGORY,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
        } else {
            trackEventFromState(state, {
                action: GA_ENTER_EMAIL_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_SOFT_ACCT_LABEL,
            });
            dispatch(sendSoftAccountActivationEmail());
        }
    } else {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_NEW_ACCT_LABEL,
        });
        dispatch(updateFormType(SIGN_UP_PATH));
    }
};

export const handleLookupUserResponseSocial = ({
    response,
    dispatch,
    getState,
    email,
    socialProvider,
}) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;
    const socialProviderChecker = socialProviderCheckers[socialProvider];

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId) {
        // If user has account linked to this social provider we return and continue with the login
        if (
            socialProviderChecker &&
            socialProviderChecker({ socialSSOSettings, signInMethods })
        ) {
            // trackEventFromState(state, {
            //     action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
            //     category: GA_AUTH_CATEGORY,
            // });

            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.reject({ message: LOOKUP_SOCIAL_USER_EXISTENT });
        }

        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEventFromState(state, {
                action: GA_ENTER_EMAIL_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_EXISTING_ACCT_LABEL,
            });

            // Returning this will redirect us to LINK_ACCOUNTS_LOG_IN_PATH, that is the look familiar
            // page with the new design.
            return Promise.reject({ message: LOOKUP_SOCIAL_USER_EXISTENT });
        } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            trackEventFromState(state, {
                action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
                category: GA_AUTH_CATEGORY,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );

            return Promise.resolve();
        } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            trackEventFromState(state, {
                action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
                category: GA_AUTH_CATEGORY,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );

            return Promise.resolve();
        } else {
            // trackEventFromState(state, {
            //     action: GA_ENTER_EMAIL_EVENT,
            //     category: GA_AUTH_CATEGORY,
            //     label: GA_SOFT_ACCT_LABEL,
            // });
            dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));

            return Promise.resolve();
        }
    } else {
        // trackEventFromState(state, {
        //     action: GA_ENTER_EMAIL_EVENT,
        //     category: GA_AUTH_CATEGORY,
        //     label: GA_NEW_ACCT_LABEL,
        // });
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));

        // The account is not existent so we ask the user what should he do.
        // Rejecting the promise will allow us to trigger the socialLoginAction
        // and store whatever we need in redis.
        return Promise.reject({ message: LOOKUP_SOCIAL_USER_UNEXISTENT });
    }
};

/** UTILS **/
export const hasFbAndAppleAccountOnly = ({
    socialSSOSettings,

    signInMethods,
}) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) &&
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) &&
    isEqual(signInMethods, [
        SIGN_IN_METHOD_FACEBOOK_LOGIN,
        SIGN_IN_METHOD_APPLE_LOGIN,
    ]);

export const hasFbAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) && includes(signInMethods, SIGN_IN_METHOD_FACEBOOK_LOGIN);

export const hasAppleAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) && includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN);

const socialProviderCheckers = {
    ['apple']: hasAppleAccount,
    ['facebook']: hasFbAccount,
    // ['google']: hasGoogleAccount,
};
